<template>
  <div class="profile-menu__dropdown-content dropdown-menu-content side-menu">
    <div v-if="isFakeLogin" class="d-flex align-items-center justify-content-between px-3 py-2 text-success">
      <span>Impersonated login as</span>
      <a href="#" class="text-danger ms-2" @click.prevent="$emit('exit-impersonation')">Exit</a>
    </div>
    <div class="profile-menu__dropdown-section dropdown-menu-section">
      <div
        class="profile-menu__user-info position-relative d-flex justify-content-start align-center px-5 py-3"
        v-if="user.email"
      >
        <div class="profile-menu__avatar">
          <avatar :username="fullName" />
        </div>
        <div class="profile-menu__details ps-3">
          <div class="profile-menu__name me-2">
            {{ fullName }}
          </div>
          <div class="profile-menu__email">
            {{ user.email }}
          </div>
          <b-badge variant="outline-tonal" class="badge-sm mt-2">
            {{ getRoleName(role) }}
          </b-badge>
        </div>
      </div>

      <template v-if="returnProfile">
        <hr class="dropdown-divider" />
        <a
          class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
          href="#"
          @click.prevent="$emit('return-profile', returnProfile.email)"
        >
          Return to {{ returnProfile.email }}
        </a>
      </template>

      <template v-if="canImpersonate">
        <hr class="dropdown-divider" />
        <a
          class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
          href="#"
          @click.prevent="$emit('open-fake-login-form')"
        >
          Login as ...
        </a>
        <a
          v-for="item in impersonationProfiles"
          class="profile-menu__dropdown-item profile-menu__dropdown-item--with-action dropdown-item dropdown-item-sm"
          href="#"
          :key="item.email"
          @click.prevent="$emit('impersonate-profile', item.email)"
          @auxclick.middle.prevent.stop="$emit('impersonate-profile-new-tab', item.email)"
          @mousedown.middle.prevent
        >
          <span>Login as {{ item.email }}</span>
          <button
            type="button"
            class="profile-menu__remove-impersonation"
            aria-label="Remove saved impersonation"
            @click.stop.prevent="$emit('remove-impersonation-profile', item.email)"
          >
            x
          </button>
        </a>
      </template>

      <hr class="dropdown-divider" />
      <ThemeSwitcher class="px-3 py-3 w-100" />
      <LanguageSwitcher class="px-3 pb-3 w-100" />

      <template v-if="filteredProfiles.length > 0 && user.email">
        <hr class="dropdown-divider" />
        <a
          v-for="item in filteredProfiles"
          class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
          href="#"
          :key="item.email"
          @click.prevent="$emit('switch-user', item.email)"
        >
          {{ $t('switchTo') }} {{ item.email }}
        </a>
      </template>

      <template v-if="preparedAvailableRoles.length > 0">
        <hr class="dropdown-divider" />
        <a
          v-for="item in preparedAvailableRoles"
          class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
          :href="item.href ?? '#'"
          :key="item.value"
          @click="handleRoleClick($event, item.value)"
        >
          {{ $t('switchTo') }} {{ getRoleName(item.value) }}
        </a>
      </template>

      <hr class="dropdown-divider" />
      <router-link
        to="/edit"
        class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
        @click.native="$emit('close-menu')"
      >
        {{ $t('settings') }}
      </router-link>
      <router-link
        v-if="$can('visit', 'Payments')"
        to="/payments"
        class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
        @click.native="$emit('close-menu')"
      >
        {{ $t('payments') }}
      </router-link>
      <a
        v-if="!isFakeLogin && newInterfaceEnabled"
        href="#"
        class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
        @click.prevent="$emit('open-new-interface')"
      >
        {{ $t('modules.campaigns.layout.header.openNewInterface') }}
      </a>
      <a
        href="#"
        class="profile-menu__dropdown-item dropdown-item dropdown-item-sm"
        @click="$emit('sign-out')"
        id="sign-out"
      >
        {{ $t('signOut') }}
      </a>
    </div>
  </div>
</template>

<script>
import { isNewInterfaceEnabled } from '@/auth/openNewInterface';
import Avatar from '@/components/Avatar.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher/LanguageSwitcher.vue';
import ThemeSwitcher from '@/components/ThemeSwitcher/ThemeSwitcher.vue';

export default {
  name: 'ProfileMenuContent',
  components: {
    Avatar,
    LanguageSwitcher,
    ThemeSwitcher,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    fullName: {
      type: String,
      required: true,
    },
    role: {
      type: String,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: true,
    },
    canImpersonate: {
      type: Boolean,
      default: false,
    },
    isFakeLogin: {
      type: Boolean,
      required: true,
    },
    returnProfile: {
      type: Object,
      default: null,
    },
    impersonationProfiles: {
      type: Array,
      default: () => [],
    },
    filteredProfiles: {
      type: Array,
      default: () => [],
    },
    preparedAvailableRoles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return { newInterfaceEnabled: isNewInterfaceEnabled() };
  },
  emits: [
    'open-fake-login-form',
    'switch-user',
    'return-profile',
    'impersonate-profile',
    'impersonate-profile-new-tab',
    'remove-impersonation-profile',
    'switch-role',
    'close-menu',
    'sign-out',
    'open-new-interface',
    'exit-impersonation',
  ],
  methods: {
    getRoleName(input) {
      if (!input) {
        return '';
      }
      let result = '';
      const words = input.split('-');
      words.forEach((word, index) => {
        if (index === 0) {
          result += word;
        } else {
          result += word.charAt(0).toUpperCase() + word.slice(1);
        }
      });
      // Assuming $t is globally available or provided via context/plugin
      return this.$t(result);
    },
    // Left-click: prevent navigation, emit switch-role for in-tab switch.
    // Middle-click / Cmd+click / Ctrl+click: let the browser open a new tab
    // natively via the href -- do NOT prevent default.
    handleRoleClick(event, roleValue) {
      const isModified = event.ctrlKey || event.metaKey || event.shiftKey;
      if (event.button !== 0 || isModified) {
        return;
      }
      event.preventDefault();
      this.$emit('switch-role', roleValue);
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-menu__dropdown-content {
  height: auto;
}

.profile-menu__avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  .vue-avatar {
    width: 4rem;
    height: 4rem;
    min-height: 4rem;
    min-width: 4rem;
  }
}

.profile-menu__details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  min-width: 0;
}
.profile-menu__name {
  font-size: 1rem;
  font-weight: 500;
}

.profile-menu__email {
  font-size: 0.9rem;
  color: var(--bs-body-color-medium);
  font-family: var(--font-roboto);
}

.profile-menu__dropdown-item--with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.profile-menu__remove-impersonation {
  border: 0;
  background: transparent;
  color: var(--bs-body-color-muted);
  font: inherit;
  line-height: 1;
  padding: 0.125rem 0.25rem;
  cursor: pointer;

  &:hover {
    color: var(--bs-danger);
  }
}
</style>
